import React, { useMemo, useState } from "react";
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Rating,
    styled,
    TextField,
    Typography,
} from "@mui/material";
import axios from "../../../axios";
import Logo from "../../../assets/logo.png";
import toast from "react-hot-toast";
import LoginIcon from "@mui/icons-material/Login";
import PhoneIcon from "@mui/icons-material/Phone";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { USER_CREATE_REVIEW } from "../../api";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAuth from "../../hooks/useAuth";

const themePrimary = "#1976d2";
const themePrimaryLight = "#e3f2fd";

const RATING_WORDS = {
    1: "poor",
    2: "fair",
    3: "good",
    4: "great",
    5: "excellent",
};

const MainBox = styled(Box)(() => ({
    "& .topHeader": {
        position: "fixed",
        width: "100%",
        backgroundColor: "#fff",
        zIndex: "999",
        boxShadow: "rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px",
        "& .logoContainer": {
            padding: "5px 0 2px 0",
            "& img": {
                width: "200px",
            },
        },
        "& .signInBtn": {
            color: themePrimary,
            fontSize: "1rem",
            fontWeight: "600",
        },
        "& .currentUser": {
            border: "none",
            background: "transparent",
            cursor: "pointer",
            padding: "0.25rem 0.4rem",
            borderRadius: "8px",
            "&:hover": {
                backgroundColor: "#f4f8fc",
            },
        },
        "& .bodyTxt": {
            color: themePrimary,
            paddingRight: "0.3rem",
        },
    },
    "& .contentMain": {
        paddingTop: "8rem",
        paddingBottom: "3rem",
    },
}));

const MAX_REVIEW_LEN = 300;

export default function RequestReview() {
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const [review, setReview] = useState("");
    const [reviewErr, setReviewErr] = useState(false);
    const [rating, setRating] = useState(null);
    const [ratingErr, setRatingErr] = useState(false);
    const [hoverStar, setHoverStar] = useState(-1);
    const [searchParams] = useSearchParams();
    const serviceId = searchParams.get("service_id") || "";
    const userName = user?.full_name?.trim()
        || `${user?.user_profile?.first_name || ""} ${user?.user_profile?.last_name || ""}`.trim()
        || user?.email
        || "Account";
    const userRole = user?.role || user?.user_profile?.role || "";
    const userInitials = userName
        .split(/\s+/)
        .map((part) => part?.[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();

    const ratingHint = useMemo(() => {
        const hoverVal = hoverStar != null && hoverStar > 0 ? Math.round(hoverStar) : 0;
        if (hoverVal >= 1 && hoverVal <= 5) return RATING_WORDS[hoverVal];
        if (rating != null && rating >= 1 && rating <= 5) return RATING_WORDS[rating];
        return "Tap a star to rate";
    }, [hoverStar, rating]);

    const handleReviewRequest = async (dataObj) => {
        toast.promise(
            axios.post(`${USER_CREATE_REVIEW}`, dataObj, {
                headers: { "Content-Type": "application/json" },
            }),
            {
                loading: () => "Creating Review Request!",
                success: (res) => {
                    window.open(`https://www.BookCleany.com`, "_self");
                    return res?.data?.message;
                },
                error: (err) => JSON.stringify(err?.message || ""),
            },
        );
    };

    const goBookCleany = () => {
        window.open(`https://www.BookCleany.com`, "_self");
    };

    return (
        <>
            <MainBox>
                <Box className="topHeader">
                    <Container maxWidth="lg">
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box className="logoContainer">
                                <a href="/dashboard/default">
                                    <img src={Logo} alt="logo" className="logoImg" style={{ cursor: "pointer" }} />
                                </a>
                            </Box>
                            <Box display="flex" alignItems="center">
                                <Box display="flex" alignItems="center">
                                    <PhoneIcon className="bodyTxt" />
                                    <Typography variant="body1" className="bodyTxt" style={{ cursor: "pointer" }}>
                                        <a href="tel:+12128516134">+1(212)851-6134</a>
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" sx={{ marginLeft: "1rem" }}>
                                    {isAuthenticated && user ? (
                                        <Box
                                            component="button"
                                            type="button"
                                            className="currentUser"
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            onClick={() => navigate("/dashboard/default")}
                                            aria-label={`Open dashboard for ${userName}`}
                                        >
                                            <Avatar
                                                src={user?.avatar}
                                                sx={{ width: 34, height: 34, bgcolor: "#0b4ca8", fontSize: 12, fontWeight: 700 }}
                                            >
                                                {userInitials}
                                            </Avatar>
                                            <Box sx={{ textAlign: "left", display: { xs: "none", sm: "block" } }}>
                                                <Typography sx={{ color: "#07154a", fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}>
                                                    {userName}
                                                </Typography>
                                                {userRole && (
                                                    <Typography sx={{ color: "#64748b", fontSize: 10, lineHeight: 1.2 }}>
                                                        {userRole}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Button
                                            variant="text"
                                            startIcon={<LoginIcon />}
                                            onClick={() => navigate(`/session/signin`)}
                                            className="signInBtn"
                                        >
                                            Customer Sign in
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Container>
                </Box>

                <Container maxWidth="sm" className="contentMain" sx={{ px: 2 }}>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: "16px",
                            border: "1px solid #e5e7eb",
                            overflow: "hidden",
                            boxShadow: "0 8px 40px rgba(25, 118, 210, 0.08), 0 1px 4px rgba(0,0,0,0.06)",
                            maxWidth: 520,
                            mx: "auto",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 2,
                                px: { xs: 2, sm: 3 },
                                py: 2.5,
                                bgcolor: themePrimaryLight,
                                borderBottom: "1px solid rgba(25, 118, 210, 0.12)",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: "50%",
                                    bgcolor: "#fff",
                                    border: `2px solid ${themePrimary}`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                <RateReviewOutlinedIcon sx={{ color: themePrimary, fontSize: 26 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: "1.25rem",
                                        color: "#0f172a",
                                        letterSpacing: "-0.02em",
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Leave a Review
                                </Typography>
                                <Typography sx={{ mt: 0.5, fontSize: "0.95rem", color: "#64748b", fontWeight: 500 }}>
                                    How was your cleaning experience?
                                </Typography>
                            </Box>
                            <Chip
                                label={`Service ID: ${serviceId || "—"}`}
                                size="small"
                                sx={{
                                    flexShrink: 0,
                                    fontWeight: 600,
                                    bgcolor: "#fff",
                                    border: `1px solid ${themePrimary}`,
                                    color: themePrimary,
                                    height: 30,
                                }}
                            />
                        </Box>

                        <CardContent sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: 3 }}>
                            <Typography
                                sx={{
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    letterSpacing: "0.12em",
                                    color: "#9ca3af",
                                    textAlign: "center",
                                    mb: 1,
                                }}
                            >
                                YOUR RATING
                            </Typography>

                            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                                <Rating
                                    name="service-rating"
                                    value={rating}
                                    precision={1}
                                    size="large"
                                    onChange={(event, newValue) => {
                                        setRatingErr(false);
                                        setRating(newValue);
                                    }}
                                    onChangeActive={(event, newHover) => {
                                        setHoverStar(newHover == null ? -1 : newHover);
                                    }}
                                    onMouseLeave={() => setHoverStar(-1)}
                                    sx={{
                                        fontSize: 36,
                                        "& .MuiRating-iconFilled": { color: themePrimary },
                                        "& .MuiRating-iconHover": { color: "#42a5f5" },
                                        "& .MuiRating-iconEmpty": {
                                            color: "#d1d5db",
                                        },
                                    }}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    textAlign: "center",
                                    fontSize: "0.9rem",
                                    color: "#6b7280",
                                    fontWeight: 500,
                                    minHeight: 24,
                                    mb: 2.5,
                                }}
                            >
                                {ratingHint}
                            </Typography>

                            <TextField
                                id="review-textarea"
                                fullWidth
                                multiline
                                minRows={4}
                                placeholder="Tell us about your experience — what went well, what could be better..."
                                value={review}
                                inputProps={{ maxLength: MAX_REVIEW_LEN }}
                                onChange={(event) => {
                                    setReviewErr(false);
                                    setReview(event.target.value.slice(0, MAX_REVIEW_LEN));
                                }}
                                error={Boolean(reviewErr)}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "12px",
                                        bgcolor: "#fafafa",
                                    },
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ display: "block", textAlign: "right", mt: 0.75, color: "#9ca3af" }}
                            >
                                {review.length} / {MAX_REVIEW_LEN}
                            </Typography>
                            {reviewErr ? (
                                <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 0.5 }}>
                                    This field may not be blank.
                                </Typography>
                            ) : null}
                            {ratingErr ? (
                                <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 0.5 }}>
                                    Please select a star rating.
                                </Typography>
                            ) : null}

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    mt: 2.5,
                                    flexWrap: "wrap",
                                    gap: 1.5,
                                }}
                            >
                                <Box sx={{ display: "flex", gap: 1.5, ml: "auto" }}>
                                    <Button
                                        variant="outlined"
                                        onClick={goBookCleany}
                                        sx={{
                                            borderRadius: "10px",
                                            textTransform: "none",
                                            fontWeight: 600,
                                            borderColor: "#d1d5db",
                                            color: "#374151",
                                            px: 2.5,
                                        }}
                                    >
                                        Skip
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            if (!isAuthenticated || userRole !== "Customer") {
                                                toast.error("Please sign in with the customer account to submit this review.");
                                                return;
                                            }
                                            let ok = true;
                                            if (!review.trim()) {
                                                setReviewErr(true);
                                                ok = false;
                                            }
                                            if (rating == null || rating < 1) {
                                                setRatingErr(true);
                                                ok = false;
                                            }
                                            if (!ok) return;
                                            const dataObj = {
                                                service: Number(serviceId),
                                                review: review.trim(),
                                                review_type: "Service",
                                                rating,
                                            };
                                            handleReviewRequest(dataObj);
                                        }}
                                        endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                                        sx={{
                                            borderRadius: "10px",
                                            textTransform: "none",
                                            fontWeight: 700,
                                            bgcolor: themePrimary,
                                            px: 2.5,
                                            boxShadow: "none",
                                            "&:hover": { bgcolor: "#1565c0", boxShadow: "none" },
                                        }}
                                    >
                                        Submit Review
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Container>
            </MainBox>
        </>
    );
}
